import { type MarketingLocale, useLocalized } from '~/lib/marketing-locale'
import { DocsIcon } from './product-icons'
import { ScrollReveal } from './scroll-reveal'
import { TruchetField } from './truchet-field'

/*
 * Documents deep-dive. The image tiles are gradient placeholders standing
 * in for real project photography at the same footprint.
 *
 * Copy is locale-keyed — see the note in `crm-section.tsx`.
 */

const PhotoTile = ({ from, to }: { from: string; to: string }) => (
  <span
    className="block size-full rounded-md"
    style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}
  />
)

const PRIORITY_CLASS = {
  high: 'bg-[#fde0d6] text-[#c4502f]',
  low: 'bg-[#dbe7fb] text-[#3b6fc4]',
  medium: 'bg-[#fdeccf] text-[#b07d24]',
} as const

type PriorityKey = keyof typeof PRIORITY_CLASS
type DocRow = { name: string; owner: string; priority: PriorityKey }
type FeatureCopy = { title: string; body: string }

type DocumentsContent = {
  heading: string
  tagline: string
  shareLabel: string
  docTitle: string
  docBody: string
  tableColumns: readonly [string, string, string]
  priorityLabels: Record<PriorityKey, string>
  rows: readonly DocRow[]
  referencesTitle: string
  referencesBody: string
  presentLabel: string
  cards: readonly [FeatureCopy, FeatureCopy, FeatureCopy]
}

const CONTENT: Record<MarketingLocale, DocumentsContent> = {
  en: {
    cards: [
      {
        body: 'Drop in a handful of site photos and watch Planno arrange them into a considered gallery.',
        title: 'Image galleries',
      },
      {
        body: 'Forget dragging text boxes around. Focus on what each sheet needs to say to the client.',
        title: 'Presentations',
      },
      {
        body: 'Covers generated for every document, so the material arrives carrying the practice’s identity.',
        title: 'Covers',
      },
    ],
    docBody:
      'A 2,600 sq ft single-family house on a corner lot. The program serves a family of four, with a home office and an outdoor kitchen opening onto the garden.',
    docTitle: 'Outline specification',
    heading: 'Documents',
    presentLabel: 'Present',
    priorityLabels: { high: 'High', low: 'Low', medium: 'Medium' },
    referencesBody: 'A few projects that informed the design approach.',
    referencesTitle: 'References',
    rows: [
      { name: 'Concept design', owner: 'Maya E.', priority: 'high' },
      { name: 'Permit set', owner: 'Ryan C.', priority: 'high' },
      { name: 'Detailing', owner: 'Chloe R.', priority: 'medium' },
    ],
    shareLabel: 'Share',
    tableColumns: ['Phase', 'Owner', 'Status'],
    tagline: 'Easy to write, handsome to hand over',
  },
  'pt-BR': {
    cards: [
      {
        body: 'Solte um punhado de fotos da obra e veja o Planno organizá-las em uma galeria caprichada.',
        title: 'Galerias de imagens',
      },
      {
        body: 'Esqueça arrastar caixas de texto. Foque no que cada prancha precisa dizer ao cliente.',
        title: 'Apresentações',
      },
      {
        body: 'Capas geradas para cada documento, para o material chegar com a identidade do escritório.',
        title: 'Capas',
      },
    ],
    docBody:
      'Residência unifamiliar de 240 m² em terreno de esquina. O programa atende uma família de quatro pessoas, com home office e área gourmet integrada ao jardim.',
    docTitle: 'Memorial descritivo',
    heading: 'Documentos',
    presentLabel: 'Apresentar',
    priorityLabels: { high: 'Alta', low: 'Baixa', medium: 'Média' },
    referencesBody: 'Alguns projetos que serviram de referência para o partido.',
    referencesTitle: 'Referências',
    rows: [
      { name: 'Estudo preliminar', owner: 'Marina A.', priority: 'high' },
      { name: 'Projeto legal', owner: 'Rafael S.', priority: 'high' },
      { name: 'Detalhamento', owner: 'Camila R.', priority: 'medium' },
    ],
    shareLabel: 'Compartilhar',
    tableColumns: ['Etapa', 'Responsável', 'Status'],
    tagline: 'Fáceis de escrever, bonitos de entregar',
  },
}

const DocMockup = ({ content }: { content: DocumentsContent }) => (
  <div className="overflow-hidden rounded-2xl border border-[#ececec] bg-white shadow-[0_24px_60px_-24px_rgba(40,30,70,0.28)]">
    <div className="flex items-center justify-end gap-2 border-[#f1f1f1] border-b px-5 py-3">
      <span className="text-[#9a9aa0] text-[12px]">{content.shareLabel}</span>
      <span className="flex h-[18px] w-[32px] items-center rounded-full bg-[#5b8def] px-[3px]">
        <span className="ml-auto size-[12px] rounded-full bg-white" />
      </span>
      <span className="text-[#c4c4c8]">⋮</span>
    </div>
    <div className="p-6">
      <p className="font-bold text-[#1c1c20] text-[20px]">{content.docTitle}</p>
      <p className="mt-2 text-[#6c6c72] text-[12.5px] leading-relaxed">{content.docBody}</p>
      <div className="mt-4 overflow-hidden rounded-lg border border-[#eee]">
        <div className="grid grid-cols-[1.4fr_1fr_0.8fr] bg-[#fafafa] text-[#8a8a90] text-[11px]">
          {content.tableColumns.map((h) => (
            <div className="border-[#eee] border-r border-b px-3 py-2 last:border-r-0" key={h}>
              {h}
            </div>
          ))}
        </div>
        {content.rows.map((row) => (
          <div className="grid grid-cols-[1.4fr_1fr_0.8fr] text-[12px]" key={row.name}>
            <div className="border-[#eee] border-r border-b px-3 py-2.5 text-[#43434a]">
              {row.name}
            </div>
            <div className="border-[#eee] border-r border-b px-3 py-2.5 text-[#43434a]">
              {row.owner}
            </div>
            <div className="border-[#eee] border-b px-3 py-2.5">
              <span
                className={`rounded px-1.5 py-0.5 text-[10.5px] ${PRIORITY_CLASS[row.priority]}`}
              >
                {content.priorityLabels[row.priority]}
              </span>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-6 font-bold text-[#1c1c20] text-[14px]">{content.referencesTitle}</p>
      <p className="mt-1 text-[#6c6c72] text-[12px]">{content.referencesBody}</p>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <div className="col-span-2 h-[120px]">
          <PhotoTile from="#9fc3e8" to="#dfe8c8" />
        </div>
        <div className="h-[120px]">
          <PhotoTile from="#c4d9a8" to="#7fae8a" />
        </div>
        <div className="h-[70px]">
          <PhotoTile from="#a8c8e8" to="#cdd9e8" />
        </div>
        <div className="h-[70px]">
          <PhotoTile from="#d8c98a" to="#a8b878" />
        </div>
        <div className="h-[70px]">
          <PhotoTile from="#8aa8c8" to="#bcc8d4" />
        </div>
      </div>
    </div>
  </div>
)

const FeatureCard = ({
  visual,
  title,
  body,
  heading: CardHeading = 'h3',
}: {
  visual: React.ReactNode
  title: string
  body: string
  /** One level below the section heading: `h2` when the section renders `h1`. */
  heading?: 'h2' | 'h3'
}) => (
  <div className="rounded-2xl border border-[#e7e3f4] bg-white/70 p-5">
    <div className="flex h-[120px] items-center justify-center overflow-hidden rounded-xl bg-[#f6f4fc]">
      {visual}
    </div>
    <CardHeading className="mt-4 font-bold text-[#1c1c20] text-[16px]">{title}</CardHeading>
    <p className="mt-1.5 text-[#8a8a90] text-[14px] leading-relaxed">{body}</p>
  </div>
)

export const DocumentsSection = ({
  headingLevel: Heading = 'h2',
}: {
  /** `h1` when the section opens a deep-dive page and owns its top heading. */
  headingLevel?: 'h1' | 'h2'
}) => {
  const content = useLocalized(CONTENT)
  const cardHeading = Heading === 'h1' ? 'h2' : 'h3'

  return (
    <section
      className="relative overflow-hidden py-28"
      style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #f1eefb 30%, #ece8f9 100%)',
      }}
    >
      <TruchetField
        className="absolute inset-0 h-full w-full text-[#0b0b0d] opacity-[0.05]"
        seed="documents"
      />
      <div className="relative mx-auto max-w-[1120px] px-6">
        <ScrollReveal className="text-center">
          <div className="flex items-center justify-center gap-3">
            <DocsIcon className="size-[56px]" />
            <Heading className="font-display text-[#0b0b0d] text-[clamp(48px,8vw,76px)] tracking-[-0.01em]">
              {content.heading}
            </Heading>
          </div>
          <p className="mt-3 text-[#8a8a90] text-[clamp(18px,2.6vw,24px)]">{content.tagline}</p>
        </ScrollReveal>

        <div className="mt-16 grid items-start gap-6 lg:grid-cols-[1.5fr_1fr]">
          <ScrollReveal>
            <DocMockup content={content} />
          </ScrollReveal>
          <ScrollReveal className="space-y-6" delay={140}>
            <FeatureCard
              body={content.cards[0].body}
              heading={cardHeading}
              title={content.cards[0].title}
              visual={
                <div className="grid w-[80%] grid-cols-2 gap-1.5">
                  <div className="h-9">
                    <PhotoTile from="#9fc3e8" to="#dfe8c8" />
                  </div>
                  <div className="h-9">
                    <PhotoTile from="#c4d9a8" to="#7fae8a" />
                  </div>
                  <div className="h-9">
                    <PhotoTile from="#a8c8e8" to="#cdd9e8" />
                  </div>
                  <div className="h-9">
                    <PhotoTile from="#d8c98a" to="#a8b878" />
                  </div>
                </div>
              }
            />
            <FeatureCard
              body={content.cards[1].body}
              heading={cardHeading}
              title={content.cards[1].title}
              visual={
                <div className="relative w-[80%] rounded-lg bg-white p-3 shadow-sm">
                  <div className="space-y-1.5">
                    <span className="block h-[6px] w-1/2 rounded-full bg-[#dcdce0]" />
                    <span className="block h-[5px] w-3/4 rounded-full bg-[#ececef]" />
                    <span className="block h-[5px] w-2/3 rounded-full bg-[#ececef]" />
                  </div>
                  <span className="absolute right-2 bottom-2 rounded-md bg-[#5b8def] px-2 py-1 font-medium text-[10px] text-white">
                    {content.presentLabel}
                  </span>
                </div>
              }
            />
            <FeatureCard
              body={content.cards[2].body}
              heading={cardHeading}
              title={content.cards[2].title}
              visual={
                <div className="flex w-[80%] gap-2">
                  {[
                    ['#cfe0f6', '#9fc3e8'],
                    ['#f6d6e6', '#e8a8c8'],
                  ].map(([a, b]) => (
                    <div
                      className="flex-1 rounded-md p-2"
                      key={a}
                      style={{ background: `linear-gradient(135deg, ${a}, ${b})` }}
                    >
                      <span className="block h-[5px] w-3/4 rounded-full bg-white/70" />
                      <span className="mt-1 block h-[4px] w-1/2 rounded-full bg-white/50" />
                    </div>
                  ))}
                </div>
              }
            />
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
